import { useCallback, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { diagnostics } from '@/utils/diagnostics';

import { useGenerateSummaryWithPromptMutation } from '../../graphql/AdminPromptIterationPage.generated';
import { useOnPromptTestRunComplete } from '../useOnPromptTestRunComplete';

export function usePreviewRunner(promptId: string) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [previewMarkdown, setPreviewMarkdown] = useState<string | null>(null);
  const [previewResetKey, setPreviewResetKey] = useState(0);
  const [isKickingOffPreview, setIsKickingOffPreview] = useState(false);

  const [generatePreview, { loading: previewMutationLoading }] =
    useGenerateSummaryWithPromptMutation({
      onError: (err) => {
        showFeedback('Failed to generate preview');
        reportError('Failed to generate preview', err);
        setIsKickingOffPreview(false);
      },
    });

  useOnPromptTestRunComplete({
    promptId,
    enabled: isKickingOffPreview,
    selectedDocumentId,
    onPreviewUpdated: (markdown) => {
      setPreviewMarkdown(markdown);
      setPreviewResetKey((v) => v + 1);
    },
    clearLoading: () => setIsKickingOffPreview(false),
  });

  const handleDocumentSelection = useCallback(
    (_event: unknown, value: string | null) => {
      setSelectedDocumentId(value || '');
      setPreviewMarkdown(null);
    },
    []
  );

  const handleGeneratePreview = useCallback(
    async (promptMarkdown: string, documentSummaryPromptID: string) => {
      if (!selectedDocumentId) {
        showFeedback('Please select a document first', { variant: 'warning' });
        return;
      }

      diagnostics.debug('[usePreviewRunner] Generating preview', {
        selectedDocumentId,
        documentSummaryPromptID,
        promptMarkdown,
      });

      setIsKickingOffPreview(true);
      await generatePreview({
        variables: {
          documentID: selectedDocumentId,
          documentSummaryPromptID,
          promptMarkdown,
        },
      });
    },
    [selectedDocumentId, generatePreview, showFeedback]
  );

  const isDisabled = useMemo(
    () => !selectedDocumentId || previewMutationLoading || isKickingOffPreview,
    [selectedDocumentId, previewMutationLoading, isKickingOffPreview]
  );

  return {
    selectedDocumentId,
    setSelectedDocumentId: handleDocumentSelection,
    previewMarkdown,
    previewResetKey,
    generatePreview: handleGeneratePreview,
    isGenerating: isKickingOffPreview || previewMutationLoading,
    isDisabled,
  };
}
